import React, { useState } from 'react';
import { styled } from '@mui/material';
import { RFieldGroup, RTextInput, RPillButton, RErrorText, RTextButton, RCaption, RText } from 'recruit/components';
import { recruitVerify, sendVerification } from 'recruit/api/candidate_auth';
import { useRecruitCandidateUser } from 'recruit/context/RecruitCandidateUserContext';

function maskPhone(phone: string) {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 4) return phone;
    return '(***) ***-' + digits.slice(-4);
}

interface CandidateVerifyFormProps {
    onVerifySuccess: () => void;
}

export function CandidateVerifyForm({ onVerifySuccess }: CandidateVerifyFormProps) {
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [resent, setResent] = useState(false);
    const { candidateUser, pendingSignup, setVerifiedVia, refreshCandidateUser } = useRecruitCandidateUser();
    const isPostLogin = !!candidateUser && !pendingSignup;
    const phoneNumber = isPostLogin ? candidateUser?.phone_number || '' : pendingSignup?.phone_number || '';

    async function handleVerify() {
        setError('');

        if (!code) {
            setError('Please enter the verification code.');
            return;
        }

        setLoading(true);
        try {
            await recruitVerify(phoneNumber, code);
            if (isPostLogin) {
                await refreshCandidateUser();
            } else {
                setVerifiedVia('sms');
            }
            onVerifySuccess();
        } catch {
            setError('Invalid verification code. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    async function handleResend() {
        setResending(true);
        setResent(false);
        try {
            await sendVerification(phoneNumber);
            setResent(true);
        } catch {
            setError('Failed to resend code. Please try again.');
        } finally {
            setResending(false);
        }
    }

    return (
        <>
            <RText component="p" lineHeight="relaxed">
                We sent a verification code to <strong>{maskPhone(phoneNumber)}</strong>. We also sent a verification
                link to your email.
            </RText>
            <FieldStack>
                <RFieldGroup label="Verification Code">
                    <RTextInput value={code} onChange={setCode} placeholder="Enter your code" fullWidth />
                </RFieldGroup>
                {error && <RErrorText>{error}</RErrorText>}
                <RPillButton onClick={handleVerify} disabled={loading}>
                    {loading ? 'Verifying...' : 'Verify'}
                </RPillButton>
            </FieldStack>
            <ResendRow>
                <RTextButton onClick={handleResend} disabled={resending}>
                    {resending ? 'Resending...' : 'Resend code'}
                </RTextButton>
                {resent && <RCaption>Code resent!</RCaption>}
            </ResendRow>
        </>
    );
}

const FieldStack = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
});

const ResendRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
});
